<template>
  <div>
    <jet-banner />
    <div class="min-h-screen">
      <nav class="bg-white border-gray-100 print:none">
        <!-- Primary Navigation Menu -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between h-14">
            <div class="flex ml-auto">
              <!-- Logo -->
              <div class="flex-shrink-0 flex items-center">
                <Link :href="route('order-index')">
                  <application-brand />
                </Link>
              </div>

              <jet-dropdown align="right" v-if="$page.props.user.is_admin" width="300">
                <template #trigger>
                  <button class="inline-flex items-center text-sm lg:mr-12 pt-5 pb-3 text-gray-500 whitespace-nowrap" :class="{ 'text-yellow-500': $page.props.debugDate }" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ currentDateTime }} Uhr
                    <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </template>
                <template #content>
                  <div class="m-2 w-64">
                    <form @submit.prevent="setDebugDate()">
                      <VueDatePicker
                        v-model="debugDate"
                        :min-date="new Date()"
                        locale="de"
                        model-type="yyyy-MM-dd"
                        format="dd.MM.yyyy"
                        preview-format="dd.MM.yyyy"
                        :enable-time-picker="false"
                        :clearable="false"
                      ></VueDatePicker>
                      <div class="flex mt-2 gap-2">
                        <button type="submit" class="mt-2 button-primary hyphens-auto basis-1/2" :disabled="!debugDate">Datum setzen</button>
                        <button type="button" @click="clearDebugDate" class="mt-2 button-secondary hyphens-auto basis-1/2" :disabled="!$page.props.debugDate">Zurücksetzen</button>
                      </div>
                    </form>
                  </div>
                </template>
              </jet-dropdown>
              <div v-else>
                <div v-if="currentDateTime" class="inline-flex items-center text-sm pt-5 pb-3 lg:mr-12 text-gray-500 whitespace-nowrap">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ currentDateTime }} Uhr
                </div>
              </div>
              <!-- Navigation Links -->
              <div class="hidden space-x-8 sm:-my-px sm:ml-10 sm:flex">
                <jet-nav-link
                  :href="route('history')"
                  :active="route().current('history')">
                  Bestellübersicht
                </jet-nav-link>

                <jet-nav-link
                  :href="route('order-index')"
                  :active="route().current('order-index')">
                  Tagesansicht
                </jet-nav-link>

                <jet-nav-link
                  v-if="$page.props.showWeekOrderForm"
                  :href="route('week-order')"
                  :active="route().current('week-order')">
                  Wochenansicht
                </jet-nav-link>

                <jet-nav-link
                  v-if="$page.props.todaysOrderId !== 0"
                  class="whitespace-nowrap"
                  :href="route('complaint-index', $page.props.todaysOrderId)"
                  :active="route().current('complaint-index')">
                  Lieferung reklamieren
                </jet-nav-link>

                <jet-dropdown align="right" width="52">
                  <template #trigger>
                    <button
                      type="button"
                      class="pt-5 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition ease-in-out duration-150"
                    >
                      <span>Informationen</span>
                      <svg
                        class="ml-2 -mr-0.5 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </template>
                  <template #content>
                    <jet-dropdown-link
                      :href="route('documents')"
                      :active="route().current('documents')"
                    >Dokumente / Downloads</jet-dropdown-link>
                    <jet-dropdown-link
                      :href="route('contacts')"
                      :active="route().current('contacts')"
                    >Ansprechpartner</jet-dropdown-link>
                  </template>
                </jet-dropdown>
              </div>
            </div>

            <div class="hidden sm:flex sm:items-center sm:ml-6">

              <!-- Settings Dropdown -->
              <div class="ml-3 relative">
                <jet-dropdown align="right" width="48">
                  <template #trigger>
                    <button
                      v-if="$page.props.jetstream.managesProfilePhotos"
                      class="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300 transition duration-150 ease-in-out"
                    >
                      <img
                        class="h-8 w-8 rounded-full object-cover"
                        :src="$page.props.user.profile_photo_url"
                        :alt="$page.props.user.name"
                      />
                    </button>

                    <span v-else class="inline-flex rounded-md">
                      <button
                        type="button"
                        class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition ease-in-out duration-150"
                      >
                        <span
                          v-if="$page.props.user.customer_nr"
                        >{{ `${$page.props.user.customer_nr} (${$page.props.user.name ?? $page.props.user.username})` }}</span>
                        <span
                          v-else
                        >{{ `${$page.props.user.name ?? $page.props.user.username}` }}</span>

                        <svg
                          class="ml-2 -mr-0.5 h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  </template>

                  <template #content>
                    <!-- Admin -->
                    <template v-if="$page.props.user.is_admin">
                      <div class="block px-4 py-2 text-xs text-gray-400">Verwaltung</div>
                      <a
                        v-if="$page.props.user.is_admin"
                        href="/backend/"
                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                      >Administration</a>
                    </template>

                    <!-- Account Management -->
                    <div class="block px-4 py-2 text-xs text-gray-400">Account verwalten</div>

                    <jet-dropdown-link :href="route('profile.show')">Profil</jet-dropdown-link>

                    <jet-dropdown-link
                      v-if="$page.props.canUpdateTeam"
                      :href="route('teams.show', $page.props.user.current_team)"
                    >Einstellungen</jet-dropdown-link>

                    <jet-dropdown-link
                      :href="route('api-tokens.index')"
                      v-if="$page.props.jetstream.hasApiFeatures"
                    >API Tokens</jet-dropdown-link>

                    <div class="border-t border-gray-100"></div>

                    <!-- Authentication -->
                    <form @submit.prevent="logout">
                      <jet-dropdown-link as="button">Abmelden</jet-dropdown-link>
                    </form>
                  </template>
                </jet-dropdown>
              </div>
            </div>

            <!-- Hamburger -->
            <div class="-mr-2 flex items-center sm:hidden">
              <button
                @click="showingNavigationDropdown = ! showingNavigationDropdown"
                class="inline-flex items-center justify-center p-2 rounded-md text-black hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
              >
                <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path
                    :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                  <path
                    :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Responsive Navigation Menu -->
        <div
          :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}"
          class="sm:hidden pl-28"
        >
          <div class="pt-2 pb-3 space-y-1">
            <jet-responsive-nav-link
              :href="route('order-index')"
              :active="route().current('order-index')">
              Bestellen
            </jet-responsive-nav-link>
            <jet-responsive-nav-link
              :href="route('history')"
              :active="route().current('history')">
              Übersicht
            </jet-responsive-nav-link>
            <jet-responsive-nav-link
                v-if="$page.props.showWeekOrderForm"
                :href="route('week-order')"
                :active="route().current('week-order')">
                Wochenansicht
            </jet-responsive-nav-link>
            <jet-responsive-nav-link
              :href="route('documents')"
              :active="route().current('documents')">
              Dokumente / Downloads
            </jet-responsive-nav-link>
            <jet-responsive-nav-link
              :href="route('contacts')"
              :active="route().current('contacts')">
              Ansprechpartner
            </jet-responsive-nav-link>
          </div>

          <!-- Responsive Settings Options -->
          <div class="pt-4 pb-1 border-t border-gray-200">
            <div class="flex items-center px-4">
              <div>
                <div class="font-medium text-base text-gray-800">
                  <span v-if="$page.props.user.customer_nr">
                    <div class="text-gray-500">
                      Kundenummer
                    </div>
                    {{ `${$page.props.user.customer_nr}` }}
                  </span>
                  <span v-else>{{ `${$page.props.user.first_name} ${$page.props.user.last_name}` }}</span>
                </div>
              </div>
            </div>

            <div class="mt-3 space-y-1">
              <jet-responsive-nav-link
                :href="route('profile.show')"
                :active="route().current('profile.show')">
                Account verwalten
              </jet-responsive-nav-link>

              <jet-responsive-nav-link
                :href="route('api-tokens.index')"
                :active="route().current('api-tokens.index')"
                v-if="$page.props.jetstream.hasApiFeatures">API Tokens</jet-responsive-nav-link>

              <!-- Authentication -->
              <form method="POST" @submit.prevent="logout">
                <jet-responsive-nav-link as="button">Abmelden</jet-responsive-nav-link>
              </form>

              <!-- Team Management -->
              <template v-if="$page.props.jetstream.hasTeamFeatures">
                <div class="border-t border-gray-200"></div>

                <div class="block px-4 py-2 text-xs text-gray-400">Einstellungen</div>

                <!-- Team Settings -->
                <jet-responsive-nav-link
                  v-if="$page.props.canUpdateTeam"
                  :href="route('teams.show', $page.props.user.current_team)"
                  :active="route().current('teams.show')"
                >Einstellungen</jet-responsive-nav-link>

                <!-- <jet-responsive-nav-link
                  :href="route('teams.create')"
                  :active="route().current('teams.create')"
                >Create New Team</jet-responsive-nav-link> -->

                <div class="border-t border-gray-200"></div>

                <!-- Team Switcher -->
                <!-- <div class="block px-4 py-2 text-xs text-gray-400">Switch Teams</div>

                <template v-for="team in $page.props.user.all_teams" :key="team.id">
                  <form @submit.prevent="switchToTeam(team)">
                    <jet-responsive-nav-link as="button">
                      <div class="flex items-center">
                        <svg
                          v-if="team.id == $page.props.user.current_team_id"
                          class="mr-2 h-5 w-5 text-green-400"
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>{{ team.name }}</div>
                      </div>
                    </jet-responsive-nav-link>
                  </form>
                </template> -->
              </template>
            </div>
          </div>
        </div>
      </nav>

      <div v-if="unauthenticated" class="p-6 bg-red-200 text-red-600 relative z-30 max-w-5xl mx-auto my-4">
        Bitte melden Sie sich neu an, Ihre Session ist abgelaufen. <a class="font-bold underline hover:no-underline" href="/login">Zur Anmeldung</a>
      </div>

      <!-- Page Heading -->
      <header class="bg-white" v-if="$slots.header">
        <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 text-center mt-16 print:mt-0">
          <div v-if="$page.props.labels['notification.warning'] && $page.props.labels['notification.warning'].length > 0" class="p-6 bg-yellow-100 text-yellow-600 relative z-30 max-w-5xl mx-auto my-4 print:none">
            {{ $page.props.labels['notification.warning'] }}
          </div>
          <slot name="header"></slot>
        </div>
      </header>

      <!-- Page Content -->
      <main>
        <slot></slot>
      </main>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { computed } from 'vue'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import axios from 'axios'

import ApplicationBrand from "@/Components/ApplicationBrand";
import JetBanner from "@/Jetstream/Banner";
import JetDropdown from "@/Jetstream/Dropdown";
import JetDropdownLink from "@/Jetstream/DropdownLink";
import JetNavLink from "@/Jetstream/NavLink";
import JetResponsiveNavLink from "@/Jetstream/ResponsiveNavLink";
import { Link } from "@inertiajs/inertia-vue3";

export default {
  components: {
    Link,
    ApplicationBrand,
    JetBanner,
    JetDropdown,
    JetDropdownLink,
    JetNavLink,
    JetResponsiveNavLink,
    VueDatePicker,
  },

  provide() {
    return {
      systemStatus:  computed(() => this.systemStatus),
    }
  },

  data() {
    return {
      showingNavigationDropdown: false,
      unauthenticated: false,
      debugDate: null,
    };
  },

  computed: {
    currentDateTime() {
      return this.systemStatus && this.systemStatus.system_time
        ? format(parseISO(this.systemStatus.system_time), 'dd.MM.yyyy HH:mm')
        : null
    }
  },

  apollo: {
    systemStatus: {
      query: gql`
        query getSystemStatus($date: Date) {
          systemStatus(date: $date) {
            authenticated
            system_time
            next_order_day
            orderable
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables () {
        return {
          date: this.$page.props.orderDate ? this.$page.props.orderDate : undefined
        }
      },

      update (data) {
        this.unauthenticated = false

        return data.systemStatus
      },

      error (err) {
        if (err.graphQLErrors || err.networkError && err.networkError.response.status === 419) {
          this.unauthenticated = true
        } else {
          console.error(err)
        }
      },
    }
  },

  created() {
    this.systemStatusTimer = setInterval(() => {
      this.$apollo.queries.systemStatus.refetch()
    }, 10000)
  },

  mounted() {
    if (this.$page.props.debugDate) {
      this.debugDate = this.$page.props.debugDate
    }
  },

  beforeUnmount() {
    this.systemStatusTimer && clearInterval(this.systemStatusTimer)
  },

  methods: {
    // switchToTeam(team) {
    //   this.$inertia.put(
    //     route("current-team.update"),
    //     {
    //       team_id: team.id,
    //     },
    //     {
    //       preserveState: false,
    //     }
    //   );
    // },

    setDebugDate() {
      axios.post(route("debug-date"), {
        date: this.debugDate,
      }).then(() => {
        this.$inertia.reload();
        this.$apollo.queries.systemStatus.refetch()
      });
    },

    clearDebugDate() {
      axios.delete(route("clear-debug-date")).then(() => {
        this.$inertia.reload();
        this.$apollo.queries.systemStatus.refetch()
        this.debugDate = null
      });
    },

    logout() {
      this.$inertia.post(route("logout"));
    },
  },
};
</script>
